import { useDigitalTrapsStore } from '@features/regional-overview/digital-traps/digital-traps.zustand';
import { createGraphLine, mergeDailyCounts, toDateKey } from '@features/regional-overview/digital-traps/utils';
import { getYesterdayCalendarDate } from '@features/regional-overview/utils/current-calendar-date.utils';
import Highcharts from 'highcharts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlightCurveStore } from '../flight-curve.zustand';
import { ALL_TRAPS_SELECTED_VALUE } from '../sidebar/sidebar.model';
import { generateDateKeysForFilter } from './graph-flight-stage/graph-flight-stage.utils';

export const useFlightCurveGraph = () => {
  const [t, i18n] = useTranslation();

  const trapGroups = useDigitalTrapsStore(state => state.trapGroups);
  const { selectedNetwork, selectedTraps, selectedDays } = useFlightCurveStore();

  const showAllNetworkTraps = useMemo(() => {
    return selectedTraps[0] === ALL_TRAPS_SELECTED_VALUE;
  }, [selectedTraps]);

  const dateKeys = useMemo(() => generateDateKeysForFilter(selectedDays), [selectedDays]);

  const chartData = useMemo(() => {
    const network = trapGroups.find(group => group.id === selectedNetwork?.id);
    if (!network || !selectedNetwork) return [];
    const networkTraps = network.trapsInGroup;
    if (!networkTraps) {
      return [];
    }

    const observedEndKey = getYesterdayCalendarDate().format('YYYY-MM-DD');

    if (showAllNetworkTraps) {
      const mergedCounts = mergeDailyCounts(networkTraps).filter(count => toDateKey(count.date) <= observedEndKey);
      const groupLine = createGraphLine({ name: selectedNetwork.title, dailyCounts: mergedCounts, dateKeys });

      return [groupLine];
    }

    const filteredTraps = networkTraps.filter(trap => selectedTraps.includes(trap.properties.id));
    const trapLines = filteredTraps.map(trap => {
      const trapLine = createGraphLine({
        name: trap.properties.name,
        dailyCounts: trap.properties.details.daily_insect_counts.filter(count => toDateKey(count.date) <= observedEndKey),
        dateKeys
      });
      return trapLine;
    });

    return trapLines;
  }, [dateKeys, trapGroups, selectedNetwork, selectedTraps, showAllNetworkTraps]);

  const chartOptions: Highcharts.Options = useMemo(() => {
    const anchorDate = getYesterdayCalendarDate();
    const anchorDateValue = new Date(anchorDate.format('YYYY-MM-DD')).getTime();
    const locale = i18n.language || 'en';
    const axisDateFormatter = new Intl.DateTimeFormat(locale, { month: 'short', day: '2-digit', timeZone: 'UTC' });
    const tooltipDateFormatter = new Intl.DateTimeFormat(locale, { month: 'long', day: '2-digit', year: 'numeric', timeZone: 'UTC' });

    return {
      chart: {
        type: 'line'
      },
      title: {
        text: undefined
      },
      xAxis: {
        type: 'datetime',
        tickWidth: 0,
        lineWidth: 0,
        tickPositioner: function () {
          const positions = [];
          const axis = this as unknown as { dataMin: number; dataMax: number };
          const dataMin = axis.dataMin;
          const dataMax = axis.dataMax;

          const tickCount = 7;

          const interval = (dataMax - dataMin) / (tickCount - 1);

          for (let i = 0; i < tickCount; i++) {
            positions.push(dataMin + i * interval);
          }

          return positions;
        },
        labels: {
          formatter: function () {
            return axisDateFormatter.format(this.value as number);
          }
        },
        plotLines: [
          {
            value: anchorDateValue,
            color: '#999999',
            width: 2,
            dashStyle: 'Dash',
            zIndex: 5,
            label: {
              text: t('general.yesterday'),
              align: 'center',
              verticalAlign: 'top',
              y: 10,
              rotation: -90,
              style: {
                color: '#666666',
                fontSize: '11px',
                fontWeight: 'bold'
              }
            }
          }
        ],
        title: {
          text: undefined
        }
      },
      yAxis: {
        title: {
          text: t('pages.regional_overview.digital_traps.flight_curve.graph_y_axis_title')
        },
        min: 0,
        allowDecimals: false
      },
      tooltip: {
        useHTML: true,
        borderRadius: 8,
        padding: 12,
        formatter: function () {
          const date = tooltipDateFormatter.format(this.x as number);
          const name = this.series.name;
          return `
              <div style="display: flex; flex-direction: column; gap: 10px;font-size: 14px;">
                ${showAllNetworkTraps ? '' : `<div><strong>${name}</strong></div>`}
                <div><strong>${date}</strong></div>
                <div>${t('pages.regional_overview.digital_traps.flight_curve.graph_tooltip')}: <strong>${this.y}</strong></div>
              </div>
          `;
        }
      },

      plotOptions: {
        series: {
          marker: { enabled: true },
          lineWidth: 2
        }
      },
      credits: { enabled: false },
      legend: {
        enabled: !showAllNetworkTraps,
        margin: 50,
        padding: 10
      },

      series: chartData
    };
  }, [t, i18n.language, showAllNetworkTraps, chartData]);

  return { chartOptions };
};
